<template>
  <m-pageframe title="FreshDesk Tools">
    <v-container fluid>

      <v-row>
        <v-col cols="12" md="6">
          <v-card elevation="2" class="fill-height d-flex flex-column">
            <v-card-title>
              <v-icon left color="primary">mdi-domain</v-icon>
              Create/Sync One Company
            </v-card-title>
            <v-card-subtitle>
              Sync a single company to FreshDesk. It will not sync any contact information for that company. Sync knob is not enforced as this is a dev tool.
              <strong>This will write to the production FreshDesk account.</strong>
            </v-card-subtitle>
            <v-card-text>
              <v-text-field v-model="company_sync.organization_id" label="Organization ID (UUID)" outlined dense
                hide-details />
            </v-card-text>
            <v-spacer></v-spacer>
            <v-card-actions class="pa-4">
              <v-spacer></v-spacer>
              <v-btn color="primary" @click="createOneCompany" :loading="loading_create_company">
                Run Sync
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>

        <v-col cols="12" md="6">
          <v-card elevation="2" class="fill-height d-flex flex-column">
            <v-card-title>
              <v-icon left color="primary">mdi-account-plus</v-icon>
              Create/Sync One Contact
            </v-card-title>
            <v-card-subtitle>
              Sync a single contact to FreshDesk. This requires all of the employee's organizations to be in Freshdesk. Sync knob is not enforced as this is a dev tool. Note we do not sync system admin accounts in the full sync or here. 
              <strong>This will write to the production FreshDesk account.</strong> 

            </v-card-subtitle>
            <v-card-text>

              <v-text-field v-model="contact_sync.employee_id" label="Employee ID (UUID)" outlined dense hide-details />
            </v-card-text>
            <v-spacer></v-spacer>
            <v-card-actions class="pa-4">
              <v-spacer></v-spacer>
              <v-btn color="primary" @click="createOneContact" :loading="loading_create_contact">
                Run Sync
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
      
      <v-row>
        <v-col cols="12">
          <v-card elevation="2">
            <v-card-title class="primary white--text">
              <v-icon dark left>mdi-magnify</v-icon>
              FreshDesk Data Explorer (Live)
              <v-spacer></v-spacer>
              <v-btn icon dark @click="refreshTables" title="Refresh current tab">
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
            </v-card-title>

            <v-tabs v-model="active_tab" background-color="primary" dark grow>
              <v-tab>Companies</v-tab>
              <v-tab>Contacts</v-tab>
            </v-tabs>

            <v-tabs-items v-model="active_tab">
              <v-tab-item>
                <v-card flat>
                  <v-card-text>
                    <v-row align="center">
                      <v-col cols="12" md="8">
                        <v-alert type="info" text prominent dense class="mb-0" icon="mdi-information-outline">
                          Search filters currently loaded data. Use pagination at the bottom to load more records.
                        </v-alert>
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-text-field v-model="company_search" label="Filter loaded companies..."
                          prepend-inner-icon="mdi-magnify" hide-details outlined dense clearable />
                      </v-col>
                    </v-row>
                  </v-card-text>

                  <v-data-table :headers="company_headers" :items="processedCompanies" :options.sync="company_options"
                    :server-items-length="company_total" :loading="loading_companies">
                    <template #item.created_at="{ item }">
                      {{ formatTimestamp(item.created_at) }}
                    </template>
                    <template #item.updated_at="{ item }">
                      {{ formatTimestamp(item.updated_at) }}
                    </template>
                  </v-data-table>
                </v-card>
              </v-tab-item>

              <v-tab-item>
                <v-card flat>
                  <v-card-text>
                    <v-row align="center">
                      <v-col cols="12" md="8">
                        <v-alert type="info" text prominent dense class="mb-0" icon="mdi-information-outline">
                          Search filters currently loaded data. Use pagination at the bottom to load more records.
                        </v-alert>
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-text-field v-model="contact_search" label="Filter loaded contacts..."
                          prepend-inner-icon="mdi-magnify" hide-details outlined dense clearable />
                      </v-col>
                    </v-row>
                  </v-card-text>

                  <v-data-table :headers="contact_headers" :items="processedContacts" :options.sync="contact_options"
                    :server-items-length="contact_total" :loading="loading_contacts">
                    <template #item.created_at="{ item }">
                      {{ formatTimestamp(item.created_at) }}
                    </template>
                    <template #item.updated_at="{ item }">
                      {{ formatTimestamp(item.updated_at) }}
                    </template>
                  </v-data-table>
                </v-card>
              </v-tab-item>
            </v-tabs-items>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </m-pageframe>
</template>

<script>
// Helper function to safely access nested properties from a string path
const getNestedValue = (obj, path) => {
  return path.split('.').reduce((acc, part) => acc && acc[part], obj);
};

export default {
  data() {
    return {
      loading_create_company: false,
      loading_create_contact: false,
      company_sync: {
        organization_id: '',
      },
      contact_sync: {
        organization_id: '',
        employee_id: '',
      },
      active_tab: 0,
      loading_companies: true,
      loading_contacts: true,
      company_headers: [
        { text: 'FD ID', value: 'id' },
        { text: 'Name', value: 'name' },
        { text: 'Industry', value: 'industry' },
        { text: 'Tier', value: 'account_tier' },
        { text: 'RC Org ID', value: 'custom_fields.red_chirp_organization_id' },
        { text: 'Created', value: 'created_at' },
        { text: 'Updated', value: 'updated_at' },
      ],
      company_items: [],
      company_total: 0,
      company_options: { page: 1, itemsPerPage: 100, sortBy: ['updated_at'], sortDesc: [true] },
      company_search: '',

      contact_headers: [
        { text: 'FD ID', value: 'id' },
        { text: 'Name', value: 'name' },
        { text: 'Email', value: 'email' },
        { text: 'FD Company ID', value: 'company_id' },
        { text: 'FD Other Companies', value: 'other_companies' },
        { text: 'RC Org ID', value: 'custom_fields.red_chirp_organization_id' },
        { text: 'RC Employee ID', value: 'custom_fields.red_chirp_employee_id' },
        { text: 'Created', value: 'created_at' },
        { text: 'Updated', value: 'updated_at' },
      ],
      contact_items: [],
      contact_total: 0,
      contact_options: { page: 1, itemsPerPage: 100, sortBy: ['updated_at'], sortDesc: [true] },
      contact_search: '',
    }
  },
  computed: {
    processedCompanies() {
      let items = [...this.company_items];

      if (this.company_search) {
        const term = this.company_search.toLowerCase();
        items = items.filter(i => (i.name || '').toLowerCase().includes(term));
      }

      const sortBy = this.company_options.sortBy;
      const sortDesc = this.company_options.sortDesc;

      if (sortBy && sortBy.length > 0) {
        items.sort((a, b) => {
          const key = sortBy[0];
          const isDesc = sortDesc[0] || false;
          let valA = getNestedValue(a, key);
          let valB = getNestedValue(b, key);

          if (key === 'created_at' || key === 'updated_at') {
            valA = valA ? new Date(valA).getTime() : -Infinity;
            valB = valB ? new Date(valB).getTime() : -Infinity;
          }

          if (valA < valB) return isDesc ? 1 : -1;
          if (valA > valB) return isDesc ? -1 : 1;
          return 0;
        });
      }
      return items;
    },
    processedContacts() {
      let items = [...this.contact_items];

      if (this.contact_search) {
        const term = this.contact_search.toLowerCase();
        items = items.filter(i => (i.name || '').toLowerCase().includes(term) || (i.email || '').toLowerCase().includes(term));
      }

      const sortBy = this.contact_options.sortBy;
      const sortDesc = this.contact_options.sortDesc;

      if (sortBy && sortBy.length > 0) {
        items.sort((a, b) => {
          const key = sortBy[0];
          const isDesc = sortDesc[0] || false;
          let valA = getNestedValue(a, key);
          let valB = getNestedValue(b, key);

          if (key === 'created_at' || key === 'updated_at') {
            valA = valA ? new Date(valA).getTime() : -Infinity;
            valB = valB ? new Date(valB).getTime() : -Infinity;
          }

          if (valA < valB) return isDesc ? 1 : -1;
          if (valA > valB) return isDesc ? -1 : 1;
          return 0;
        });
      }
      return items;
    }
  },
  watch: {
    company_options: {
      handler(newVal, oldVal) {
        if (!oldVal || newVal.page !== oldVal.page || newVal.itemsPerPage !== oldVal.itemsPerPage) {
          this.fetchCompanies();
        }
      },
      deep: true,
      immediate: true,
    },
    contact_options: {
      handler(newVal, oldVal) {
        if (!oldVal || newVal.page !== oldVal.page || newVal.itemsPerPage !== oldVal.itemsPerPage) {
          this.fetchContacts();
        }
      },
      deep: true,
      immediate: true,
    },
    company_search() { this.company_options.page = 1; },
    contact_search() { this.contact_options.page = 1; }
  },
  methods: {
    fetchCompanies() {
      this.loading_companies = true;
      const { page, itemsPerPage } = this.company_options;
      const per_page = itemsPerPage === -1 ? 0 : itemsPerPage;

      this.$rc.rest({
        endpoint: 'LIST: /api/v1/integrations/freshdesk/companies',
        params: { page, per_page },
        spinner: false,
        on_success: (r) => {
          this.company_items = r.results || [];
          this.company_total = (r.has_next ? page * per_page + 1 : (page - 1) * per_page + this.company_items.length);
          this.loading_companies = false;
        },
        on_error: () => {
          this.loading_companies = false;
        }
      });
    },
    fetchContacts() {
      this.loading_contacts = true;
      const { page, itemsPerPage } = this.contact_options;
      const per_page = itemsPerPage === -1 ? 0 : itemsPerPage;

      this.$rc.rest({
        endpoint: 'LIST: /api/v1/integrations/freshdesk/contacts',
        params: { page, per_page },
        spinner: false,
        on_success: (r) => {
          // **FIXED**: Reverted to r.results to match API helper's output
          this.contact_items = r.results || [];
          // **FIXED**: Reverted to r.has_next
          this.contact_total = (r.has_next ? page * per_page + 1 : (page - 1) * per_page + this.contact_items.length);
          this.loading_contacts = false;
        },
        on_error: () => {
          this.loading_contacts = false;
        }
      });
    },
    refreshTables() {
      if (this.active_tab === 0) this.fetchCompanies();
      else if (this.active_tab === 1) this.fetchContacts();
    },
    createOneCompany() {
      this.loading_create_company = true;
      this.$rc.rest({
        endpoint: 'POST: /api/v1/integrations/freshdesk/create-one-company',
        params: { organization_id: this.company_sync.organization_id },
        on_success: () => {
          this.$rc.alert('OK', 'Finished sync one company');
          if (this.active_tab === 0) this.fetchCompanies();
          this.loading_create_company = false;
        },
        on_error: () => {
          this.$rc.alert('Error', 'Failed to run sync one company');
          this.loading_create_company = false;
        }
      });
    },
    createOneContact() {
      this.loading_create_contact = true;
      //This one can take a while since it's lists all orgs/contacts like the full sync
      this.$rc.rest({
        endpoint: 'POST: /api/v1/integrations/freshdesk/create-one-contact',
        params: {
          employee_id: this.contact_sync.employee_id
        },
        on_success: () => {
          this.$rc.alert('OK', 'Finshed sync one contact');
          if (this.active_tab === 1) this.fetchContacts();
          this.loading_create_contact = false;
        },
        on_error: () => {
          this.$rc.alert('Error', 'Failed to run sync one contact');
          this.loading_create_contact = false;
        },
        spinner: false,
        timeout: 300000,
      });
    },
    formatTimestamp(timestamp) {
      if (!timestamp) return '—';
      try {
        const date = new Date(timestamp);
        if (isNaN(date)) return timestamp;
        return date.toLocaleString();
      } catch (error) {
        return timestamp;
      }
    }
  }
}
</script>

<style scoped>
/* Optional: Add a subtle transition for tab items for a smoother feel */
.v-tabs-items {
  transition: all 0.3s ease-in-out;
}
</style>